import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, X, Plus } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import API_BASE_URL from '../../config/api';

/**
 * SocialVoiceForm
 * Reusable setup form for the four Social Post personalization questions.
 *
 * Used in two places:
 *   - The new onboarding step (`SocialVoiceScreen`)
 *   - The Branding Profile page ("Social voice" section)
 *
 * Talks directly to GET / PUT /api/social/setup. Does not touch OnboardingContext.
 */

const TONES = [
  { label: 'Professional and polished', value: 'professional' },
  { label: 'Friendly and conversational', value: 'friendly' },
  { label: 'Luxury and high-end', value: 'luxury' },
  { label: 'Educational and trustworthy', value: 'educational' },
  { label: 'Bold and direct', value: 'bold' },
  { label: 'Personal and relationship-driven', value: 'personal' },
];

const TARGETS = [
  { label: 'First-time buyers', value: 'first_time_buyers' },
  { label: 'Luxury buyers', value: 'luxury_buyers' },
  { label: 'Sellers', value: 'sellers' },
  { label: 'Investors', value: 'investors' },
  { label: 'Relocations', value: 'relocations' },
  { label: 'Commercial', value: 'commercial' },
  { label: 'Families upgrading', value: 'families_upgrading' },
  { label: 'Downsizers / Retirees', value: 'downsizers_retirees' },
];

const HUMORS = [
  { label: 'Witty', value: 'witty' },
  { label: 'Light', value: 'light' },
  { label: 'Mostly professional', value: 'mostly_professional' },
  { label: 'None', value: 'none' },
];

const MAX_TARGETS = 3;
const MAX_MARKETS = 5;
const MAX_MARKET_LEN = 100;

// Auto-dismiss helper
const AUTO_DISMISS_MS = 2000;

/**
 * Visual treatments are tuned to match BrandingProfilePage cards.
 *  - rounded-full pills for tone/clients/humor
 *  - rounded-md tags for market chips
 */
const chipBaseRounded = 'rounded-full px-3 py-2 text-sm font-medium border transition-colors select-none';
const chipSelectedSingle = 'bg-primary text-white border-primary';
const chipSelectedMulti = 'bg-emerald-50 text-gray-900 border-primary';
const chipUnselected = 'bg-white text-gray-900 border-gray-300 hover:border-gray-400';

const SocialVoiceForm = ({ onSaved, hideHeader = false }) => {
  const [tone, setTone] = useState('');
  const [targetClients, setTargetClients] = useState([]);
  const [humorLevel, setHumorLevel] = useState('');
  const [primaryMarkets, setPrimaryMarkets] = useState([]);
  const [marketDraft, setMarketDraft] = useState('');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [maxTargetsMsg, setMaxTargetsMsg] = useState('');
  const [marketHint, setMarketHint] = useState('');

  const maxTargetsTimer = useRef(null);
  const marketHintTimer = useRef(null);

  // ---------------------------------------------------------------
  // Initial load
  // ---------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}/api/social/setup`, {
          withCredentials: true,
        });
        if (cancelled) return;
        setTone(data?.tone || '');
        setTargetClients(Array.isArray(data?.targetClients) ? data.targetClients : []);
        setHumorLevel(data?.humorLevel || '');
        setPrimaryMarkets(Array.isArray(data?.primaryMarkets) ? data.primaryMarkets : []);
      } catch (err) {
        // 401 / network / 404 → fall back silently to empty defaults
        console.warn('[SocialVoiceForm] could not load /api/social/setup:', err?.response?.status, err?.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (maxTargetsTimer.current) clearTimeout(maxTargetsTimer.current);
      if (marketHintTimer.current) clearTimeout(marketHintTimer.current);
    };
  }, []);

  // ---------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------
  const flashMaxTargets = () => {
    setMaxTargetsMsg('Max 3 — tap a selected one to remove first');
    if (maxTargetsTimer.current) clearTimeout(maxTargetsTimer.current);
    maxTargetsTimer.current = setTimeout(() => setMaxTargetsMsg(''), AUTO_DISMISS_MS);
  };

  const flashMarketHint = (msg) => {
    setMarketHint(msg);
    if (marketHintTimer.current) clearTimeout(marketHintTimer.current);
    marketHintTimer.current = setTimeout(() => setMarketHint(''), AUTO_DISMISS_MS);
  };

  const toggleTone = (val) => setTone((prev) => (prev === val ? '' : val));
  const toggleHumor = (val) => setHumorLevel((prev) => (prev === val ? '' : val));

  const toggleTarget = (val) => {
    setTargetClients((prev) => {
      if (prev.includes(val)) return prev.filter((v) => v !== val);
      if (prev.length >= MAX_TARGETS) {
        flashMaxTargets();
        return prev;
      }
      return [...prev, val];
    });
  };

  const addMarket = () => {
    const trimmed = marketDraft.trim();
    if (!trimmed) return;
    if (trimmed.length > MAX_MARKET_LEN) {
      flashMarketHint(`Markets must be ${MAX_MARKET_LEN} characters or fewer`);
      return;
    }
    if (primaryMarkets.length >= MAX_MARKETS) return; // input is disabled, but be defensive
    if (primaryMarkets.some((m) => m.toLowerCase() === trimmed.toLowerCase())) {
      flashMarketHint('Already in your list');
      return;
    }
    setPrimaryMarkets((prev) => [...prev, trimmed]);
    setMarketDraft('');
  };

  const removeMarket = (idx) => {
    setPrimaryMarkets((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleMarketKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      // Only intercept Tab if there's a draft to add — otherwise let it move focus
      if (e.key === 'Tab' && !marketDraft.trim()) return;
      e.preventDefault();
      addMarket();
    }
  };

  const handleSave = async () => {
    setErrorMsg('');
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${API_BASE_URL}/api/social/setup`,
        { tone, targetClients, humorLevel, primaryMarkets },
        { withCredentials: true },
      );
      toast.success('Saved');
      if (typeof onSaved === 'function') onSaved(data);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status && status >= 400 && status < 500 && detail) {
        setErrorMsg(typeof detail === 'string' ? detail : 'Please double-check your selections.');
      } else {
        setErrorMsg("Couldn't save — try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------
  if (loading) {
    return (
      <div className="flex items-center justify-center py-10" data-testid="social-voice-form-loading">
        <Loader2 className="w-5 h-5 text-primary animate-spin" />
      </div>
    );
  }

  const marketsAtMax = primaryMarkets.length >= MAX_MARKETS;

  return (
    <div className="space-y-6" data-testid="social-voice-form">
      {!hideHeader && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900">How should your posts sound?</h2>
          <p className="text-sm text-gray-500 mt-1">Used to personalize Social Post.</p>
        </div>
      )}

      {/* --- Q1: Tone --- */}
      <div data-testid="q-tone">
        <div className="mb-1 text-sm font-semibold text-gray-900">Tone</div>
        <div className="text-xs text-gray-500 mb-3">Pick the one closest to you</div>
        <div className="flex flex-wrap gap-2">
          {TONES.map(({ label, value }) => {
            const selected = tone === value;
            return (
              <button
                key={value}
                type="button"
                onClick={() => toggleTone(value)}
                className={`${chipBaseRounded} ${selected ? chipSelectedSingle : chipUnselected}`}
                aria-pressed={selected}
                data-testid={`tone-chip-${value}`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* --- Q2: Target clients --- */}
      <div data-testid="q-targets">
        <div className="mb-1 text-sm font-semibold text-gray-900">Target clients</div>
        <div className="text-xs text-gray-500 mb-3">Pick up to 3</div>
        <div className="flex flex-wrap gap-2">
          {TARGETS.map(({ label, value }) => {
            const selected = targetClients.includes(value);
            return (
              <button
                key={value}
                type="button"
                onClick={() => toggleTarget(value)}
                className={`${chipBaseRounded} ${selected ? chipSelectedMulti : chipUnselected}`}
                aria-pressed={selected}
                data-testid={`target-chip-${value}`}
              >
                {label}
              </button>
            );
          })}
        </div>
        {maxTargetsMsg && (
          <div
            className="text-xs text-amber-700 mt-2"
            role="status"
            data-testid="max-targets-msg"
          >
            {maxTargetsMsg}
          </div>
        )}
      </div>

      {/* --- Q3: Humor --- */}
      <div data-testid="q-humor">
        <div className="mb-3 text-sm font-semibold text-gray-900">Humor</div>
        <div className="flex flex-wrap gap-2">
          {HUMORS.map(({ label, value }) => {
            const selected = humorLevel === value;
            return (
              <button
                key={value}
                type="button"
                onClick={() => toggleHumor(value)}
                className={`${chipBaseRounded} ${selected ? chipSelectedSingle : chipUnselected}`}
                aria-pressed={selected}
                data-testid={`humor-chip-${value}`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* --- Q4: Primary markets --- */}
      <div data-testid="q-markets">
        <div className="mb-1 text-sm font-semibold text-gray-900">Primary markets</div>
        <div className="text-xs text-gray-500 mb-3">
          Up to 5. Type a city or area and press Enter to add.
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {primaryMarkets.map((m, idx) => (
            <span
              key={`${m}-${idx}`}
              className="inline-flex items-center gap-1.5 rounded-md border border-primary bg-emerald-50 px-2.5 py-1 text-sm text-gray-900"
              data-testid={`market-chip-${idx}`}
            >
              {m}
              <button
                type="button"
                onClick={() => removeMarket(idx)}
                className="text-gray-500 hover:text-red-600"
                aria-label={`Remove ${m}`}
                data-testid={`market-chip-remove-${idx}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          ))}

          <div className="flex flex-1 min-w-[180px] gap-2">
            <Input
              value={marketDraft}
              onChange={(e) => setMarketDraft(e.target.value)}
              onKeyDown={handleMarketKeyDown}
              disabled={marketsAtMax}
              placeholder={marketsAtMax ? '' : 'e.g. Bradenton, FL'}
              maxLength={MAX_MARKET_LEN}
              className="h-9 text-sm flex-1"
              data-testid="market-input"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addMarket}
              disabled={marketsAtMax || !marketDraft.trim()}
              className="h-9 px-3"
              data-testid="market-add-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add
            </Button>
          </div>
        </div>

        {marketsAtMax && (
          <div className="text-xs text-gray-500 mt-2">Max 5 markets</div>
        )}
        {marketHint && (
          <div className="text-xs text-amber-700 mt-2" role="status" data-testid="market-hint">
            {marketHint}
          </div>
        )}
      </div>

      {/* --- Save row --- */}
      <div className="pt-2 space-y-2">
        {errorMsg && (
          <div
            className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
            role="alert"
            data-testid="save-error"
          >
            {errorMsg}
          </div>
        )}
        <Button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="bg-primary hover:bg-primary/90 text-white"
          data-testid="social-voice-save-btn"
        >
          {saving ? (
            <span className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Saving…
            </span>
          ) : (
            'Save'
          )}
        </Button>
      </div>
    </div>
  );
};

export default SocialVoiceForm;
